import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Alert, Spinner, Modal } from "react-bootstrap";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import PricingCards from "./PricingCards";
import ThemeLogo from './ThemeLogo';

// Helper function to get plan price based on subscription name
const getPlanPrice = (subscriptionName) => {
  switch (subscriptionName?.toLowerCase()) {
    case "standard":
      return "14.99";
    case "pro":
      return "24.99";
    case "ultimate":
      return "34.99";
    default:
      return "24.99"; // Default to Pro plan price
  }
};

// Helper function to get plan name from Stripe price ID
const getPlanNameFromPriceId = (priceId) => {
  if (!priceId) return "Pro";
  
  const priceIdLower = priceId.toLowerCase();
  if (priceIdLower.includes("standard")) return "Standard";
  if (priceIdLower.includes("pro")) return "Pro";
  if (priceIdLower.includes("ultimate")) return "Ultimate";
  
  return "Pro"; // Default
};

// Helper function to get plan type from Stripe price ID
const getPlanTypeFromPriceId = (priceId) => {
  if (!priceId) return "Full-Time Assessors";
  
  const priceIdLower = priceId.toLowerCase();
  if (priceIdLower.includes("monthly")) return "Monthly";
  if (priceIdLower.includes("yearly")) return "Yearly";
  
  return "Monthly"; // Default
};

export default function Subscriptions() {
  const [showPlans, setShowPlans] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [stripeSubscription, setStripeSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const navigate = useNavigate();

  const fetchStripeSubscription = async (userEmail) => {
    try {
      const response = await fetch(
        "https://us-central1-epcspark-3ce7a.cloudfunctions.net/getStripeSubscription",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ customerEmail: userEmail }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Stripe subscription data:", data);
      return data;
    } catch (error) {
      console.error("Error fetching Stripe subscription:", error);
      return null;
    }
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    setShowCancelModal(false);
    await cancelSubscription();
  };

  const cancelSubscription = async () => {
    if (!stripeSubscription?.subscription?.id) {
      setCancelError('No subscription found to cancel.');
      return;
    }

    setCancelling(true);
    setCancelError('');

    try {
      const response = await fetch(
        "https://us-central1-epcspark-3ce7a.cloudfunctions.net/cancelStripeSubscription",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            subscriptionId: stripeSubscription.subscription.id,
            customerEmail: subscriptionData?.email 
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log("Subscription cancellation result:", result);
      
      setCancelSuccess(true);
      
      // Refresh subscription data after cancellation
      setTimeout(() => {
        setCancelSuccess(false);
        checkSubscription();
      }, 3000);
      
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      setCancelError(error.message || 'Failed to cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const checkSubscription = async () => {
    try {
      if (auth.currentUser) {
        console.log("Checking subscription for user:", auth.currentUser.uid);
        const userDoc = doc(db, "Users", auth.currentUser.uid);
        const userSnapshot = await getDoc(userDoc);

        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();
          console.log("User data from Firestore:", userData);
          
          // Fetch Stripe subscription details
          if (userData.email) {
            const stripeData = await fetchStripeSubscription(userData.email);
            if (stripeData?.hasSubscription && stripeData?.subscription) {
              setStripeSubscription(stripeData);
              setHasSubscription(true);
              
              // Update subscription data with Stripe information
              const planName = getPlanNameFromPriceId(stripeData.subscription.price?.id);
              const planType = getPlanTypeFromPriceId(stripeData.subscription.price?.id);
              
              setSubscriptionData({
                ...userData,
                subscriptionName: planName,
                subscriptionType: planType,
                isTrial: stripeData.subscription.isTrialing || false,
                trialEndDate: stripeData.subscription.trial_end ? 
                  new Date(stripeData.subscription.trial_end * 1000) : null,
                subscriptionStatus: stripeData.subscription.status
              });
            } else {
              setHasSubscription(false);
              setSubscriptionData(userData);
            }
          }
        } else {
          console.log("User document does not exist");
        }
      } else {
        console.log("No authenticated user found");
      }
    } catch (error) {
      console.error("Error checking subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkSubscription();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log("User is authenticated");
        checkSubscription();
      } else {
        console.log("User is not authenticated");
        setHasSubscription(false);
        setSubscriptionData(null);
        setStripeSubscription(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Check if subscription is cancelled
  const isSubscriptionCancelled = stripeSubscription?.subscription?.cancel_at_period_end;

  return (
    <div
      style={{
        background: "linear-gradient(135deg, var(--primary-500) 0%, var(--cyan-400) 50%, var(--primary-600) 100%)",
        minHeight: "100vh",
        padding: 0,
        position: "relative",
      }}
    >
      <span
        onClick={() => navigate("/")}
        style={{
          position: "absolute",
          top: 18,
          right: 24,
          fontSize: 22,
          color: "#888",
          cursor: "pointer",
          zIndex: 10,
          fontWeight: 400,
          lineHeight: 1,
          transition: "color 0.2s",
        }}
        aria-label="Close subscriptions"
        title="Close"
      >
        &times;
      </span>
      <Container
        fluid
        style={{ maxWidth: 1100, margin: "0 auto", padding: "0" }}
      >
        {/* Logo and Logout */}
        <Row className="align-items-center mb-4" style={{ paddingTop: 32 }}>
          <Col xs="auto">
            <ThemeLogo style={{ width: 100, height: 100, marginRight: 12 }} alt="Logo" />
            <span
              style={{
                fontWeight: 700,
                fontSize: 28,
                verticalAlign: "middle",
                color: "var(--text)",
                letterSpacing: 1,
              }}
            >
              EPCSPARK
            </span>
          </Col>
        </Row>
        {/* Tabs */}
        <Row>
          <Col xs={12}>
            <div
              className="profile-tabs mb-4 mt-2"
              style={{
                display: "flex",
                gap: 32,
                borderBottom: "1px solid var(--border)",
              }}
            >
              <button
                className="profile-tab-btn active"
                type="button"
                style={{
                  fontWeight: 700,
                  fontSize: "1.13rem",
                  color: "var(--primary-500)",
                  background: "none",
                  border: "none",
                  outline: "none",
                  padding: 0,
                  margin: 0,
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                Subscriptions
              </button>
              <button
                className="profile-tab-btn"
                onClick={() => navigate("/profile")}
                type="button"
                style={{
                  fontWeight: 700,
                  fontSize: "1.13rem",
                  color: "var(--text)",
                  background: "none",
                  border: "none",
                  outline: "none",
                  padding: 0,
                  margin: 0,
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                Profile
              </button>
              <button
                className="profile-tab-btn"
                onClick={() => navigate("/change-password")}
                type="button"
                style={{
                  fontWeight: 700,
                  fontSize: "1.13rem",
                  color: "var(--text)",
                  background: "none",
                  border: "none",
                  outline: "none",
                  padding: 0,
                  margin: 0,
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                Change Password
              </button>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2
                style={{
                  fontWeight: 700,
                  fontSize: "2rem",
                  color: "var(--text)",
                  marginBottom: 0,
                }}
              >
                Subscriptions
              </h2>
              <Button
                onClick={checkSubscription}
                style={{
                  background: "linear-gradient(135deg, var(--primary-500) 0%, var(--cyan-400) 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 600,
                  padding: "8px 16px",
                  borderRadius: 8,
                }}
              >
                Refresh
              </Button>
            </div>
          </Col>
        </Row>
        <Row className="justify-content-center" style={{ marginTop: 8 }}>
          <Col md={10}>
            {!loading && (
              <>
                {!hasSubscription ? (
                  // Purchase Now Box - shown when user has no active subscription
                  <div
                    style={{
                      background: "var(--surface-2)",
                      border: "1px solid var(--border)",
                      borderRadius: 16,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                      padding: "32px 32px 24px 32px",
                      marginBottom: 24,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: "1.35rem",
                            color: "var(--text)",
                            marginBottom: 2,
                          }}
                        >
                          If you haven't purchased any plan, then purchase now.
                        </div>
                      </div>
                      <Button
                        onClick={() => setShowPlans(true)}
                        style={{
                          background: "linear-gradient(135deg, var(--primary-500) 0%, var(--cyan-400) 100%)",
                          border: "none",
                          color: "white",
                          fontWeight: 600,
                          padding: "8px 16px",
                          borderRadius: 8,
                        }}
                      >
                        Purchase Now
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Current Plan Box - shown when user has an active subscription
                  <div
                    style={{
                      background: "var(--surface-2)",
                      border: "1px solid var(--border)",
                      borderRadius: 16,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                      padding: "24px",
                      marginBottom: 16,
                    }}
                  >
                    {cancelSuccess && (
                      <Alert variant="success" style={{ marginBottom: 16, fontWeight: 600 }}>
                        Subscription cancelled successfully! Your access will continue until the end of the current billing period.
                      </Alert>
                    )}
                    {cancelError && (
                      <Alert variant="danger" style={{ marginBottom: 16, fontWeight: 600 }}>
                        {cancelError}
                      </Alert>
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: "1.35rem",
                            color: "var(--text)",
                            marginBottom: 2,
                          }}
                        >
                          Current Plan:{" "}
                          {subscriptionData?.subscriptionName || "Pro"}
                          {stripeSubscription?.subscription?.isTrialing && (
                            <span
                              style={{
                                background: "#33ce85",
                                color: "#fff",
                                fontSize: "0.8rem",
                                padding: "4px 8px",
                                borderRadius: "12px",
                                marginLeft: "8px",
                                fontWeight: "600",
                              }}
                            >
                              TRIAL
                            </span>
                          )}
                          {isSubscriptionCancelled && (
                            <span
                              style={{
                                background: "#dc3545",
                                color: "#fff",
                                fontSize: "0.8rem",
                                padding: "4px 8px",
                                borderRadius: "12px",
                                marginLeft: "8px",
                                fontWeight: "600",
                              }}
                            >
                              CANCELLED
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            color: "var(--text-secondary)",
                            fontWeight: 500,
                            fontSize: "1.05rem",
                            marginBottom: 8,
                          }}
                        >
                          {stripeSubscription?.subscription?.isTrialing
                            ? "30-Day Free Trial"
                            : subscriptionData?.subscriptionType ||
                              "Full-Time Assessors"}
                        </div>
                        {stripeSubscription?.subscription?.isTrialing ? (
                          <div
                            style={{
                              color: "#33ce85",
                              fontWeight: 700,
                              fontSize: "1.35rem",
                              marginBottom: 8,
                            }}
                          >
                            FREE{" "}
                            <span
                              style={{
                                color: "var(--text-secondary)",
                                fontWeight: 500,
                                fontSize: "1.05rem",
                              }}
                            >
                              / month
                            </span>
                          </div>
                        ) : (
                          <div
                            style={{
                              color: "var(--primary-500)",
                              fontWeight: 700,
                              fontSize: "1.35rem",
                              marginBottom: 8,
                            }}
                          >
                            &pound;
                            {stripeSubscription?.subscription?.price?.unit_amount 
                              ? (stripeSubscription.subscription.price.unit_amount / 100).toFixed(2)
                              : getPlanPrice(subscriptionData?.subscriptionName)
                            }{" "}
                            <span
                              style={{
                                color: "var(--text-secondary)",
                                fontWeight: 500,
                                fontSize: "1.05rem",
                              }}
                            >
                              / {subscriptionData?.subscriptionType?.toLowerCase() === "yearly" ? "year" : "month"}
                            </span>
                          </div>
                        )}
                        {stripeSubscription?.subscription?.isTrialing && 
                         stripeSubscription?.subscription?.trial_end && (
                          <div
                            style={{
                              color: "var(--text-secondary)",
                              fontSize: "0.9rem",
                              marginBottom: 0,
                            }}
                          >
                            Trial ends:{" "}
                            {new Date(stripeSubscription.subscription.trial_end * 1000).toLocaleDateString()}
                          </div>
                        )}
                        {stripeSubscription?.subscription?.price && (
                          <div
                            style={{
                              color: "var(--text-secondary)",
                              fontSize: "0.9rem",
                              marginBottom: 0,
                            }}
                          >
                            Amount: £
                            {(
                              stripeSubscription.subscription.price
                                .unit_amount / 100
                            ).toFixed(2)}{" "}
                            {stripeSubscription.subscription.price.currency.toUpperCase()}
                          </div>
                        )}
                        {stripeSubscription?.subscription?.current_period_end && !stripeSubscription?.subscription?.isTrialing && (
                          <div
                            style={{
                              color: "var(--text-secondary)",
                              fontSize: "0.9rem",
                              marginBottom: 0,
                            }}
                          >
                            Next billing: {new Date(stripeSubscription.subscription.current_period_end * 1000).toLocaleDateString()}
                          </div>
                        )}
                        {isSubscriptionCancelled && (
                          <div
                            style={{
                              color: "#dc3545",
                              fontWeight: 600,
                              fontSize: "0.9rem",
                              marginBottom: 0,
                            }}
                          >
                            ⚠️ Subscription will cancel at period end
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 16,
                          alignItems: "stretch",
                        }}
                      >
                        <Button
                          onClick={() => setShowPlans((v) => !v)}
                          style={{
                            background: showPlans ? "#ff4d4f" : "var(--surface-2)",
                            color: showPlans ? "#fff" : "var(--text)",
                            border: "1.5px solid var(--border)",
                            borderRadius: 12,
                            fontWeight: 600,
                            fontSize: 18,
                            padding: "12px 16px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                          }}
                        >
                          {showPlans ? "Close Plans" : "Change Plan"}
                        </Button>
                        {!isSubscriptionCancelled && (
                          <Button
                            onClick={handleCancelClick}
                            disabled={cancelling}
                            style={{
                              background: cancelling ? "#6c757d" : "#dc3545",
                              color: "#fff",
                              border: "none",
                              borderRadius: 12,
                              fontWeight: 600,
                              fontSize: 18,
                              padding: "8px 32px",
                              boxShadow: "0 2px 8px rgba(220,53,69,0.2)",
                              opacity: cancelling ? 0.7 : 1,
                              cursor: cancelling ? "not-allowed" : "pointer",
                            }}
                          >
                            {cancelling ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  style={{ marginRight: "8px" }}
                                />
                                Cancelling...
                              </>
                            ) : (
                              "Cancel Subscription"
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </Col>
        </Row>
        {showPlans && (
          <div style={{ marginTop: 0, position: "relative" }}>
            <PricingCards buttonText="Buy Now" />
          </div>
        )}
      </Container>

      {/* Cancel Confirmation Modal */}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
        style={{ zIndex: 3000 }}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: 600, color: "var(--text)" }}>
            Cancel Subscription
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "1.1rem", color: "var(--text-secondary)", marginBottom: 16 }}>
            Are you sure you want to cancel your subscription?
          </p>
          <p style={{ fontSize: "1rem", color: "var(--text-secondary)", marginBottom: 0 }}>
            Your access will continue until the end of the current billing period. 
            You can reactivate your subscription at any time.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCancelModal(false)}
            style={{
              background: "var(--surface-2)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              fontWeight: 600,
            }}
          >
            Keep Subscription
          </Button>
          <Button
            variant="danger"
            onClick={handleCancelConfirm}
            disabled={cancelling}
            style={{
              background: "linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)",
              border: "none",
              color: "white",
              borderRadius: 8,
              fontWeight: 600,
            }}
          >
            {cancelling ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  style={{ marginRight: "8px" }}
                />
                Cancelling...
              </>
            ) : (
              "Yes, Cancel Subscription"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .profile-tab-btn.active {
          color: var(--primary-500) !important;
        }
        .profile-tab-btn.active::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -8px;
          width: 100%;
          height: 3px;
          background: var(--primary-500);
          border-radius: 2px;
        }
        .profile-tab-btn:not(.active):hover::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -8px;
          width: 100%;
          height: 3px;
          background: var(--primary-500);
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
}
